import React, {useMemo} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowsUpDown, faTrash} from "@fortawesome/free-solid-svg-icons";

// Components
import PhoneNumbers from "../../components/PhoneNumbers.js";
import Marquee from "../general/Marquee.js";

// Style
import {z} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {border, pad, radius} from "../../style/components/variables";
import {Abbr, AnchorInline, HeadingSmall, Text} from "../../style/components/general.js";

const ContactReorderPreview = ({contacts, reorder, onRemove}) => {
  const grouped = useMemo(() => {
    const result = {};
    contacts.forEach(contact => {
      const department = contact.department?.name || "Contact";
      const company = contact.company?.name || "No Company Provided";
      const formatted = {...contact, department, companyName: contact.company?.name || "No Company"};
      if (!result[department]) result[department] = {[company]: [formatted]};
      else if (!result[department][company]) result[department][company] = [formatted];
      else result[department][company].push(formatted);
    });
    return result;
  }, [contacts]);

  const draggable = (id, index, content) => (
    <Draggable draggableId={id} index={index} key={id} isDragDisabled={reorder.disabled}>
      {provided => (
        <DraggableRow
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}>
          <RowBody>{content}</RowBody>
          <DragIndicator>
            <FontAwesomeIcon icon={faArrowsUpDown} />
          </DragIndicator>
        </DraggableRow>
      )}
    </Draggable>
  );

  const renderContact = (contact, index) => {
    const content = (
      <Content key={contact.email}>
        <div>
          <NoteText quiet>
            <Abbr title={`${contact.firstName} ${contact.lastName}`}>
              {contact.firstName} {contact.lastName}
            </Abbr>
          </NoteText>
          {contact.primary && contact.phones?.length > 0 && (
            <PhoneNumbers primary={contact.primary} phones={contact.phones} disabled />
          )}
          <NoteLink
            href={`mailto:${contact.email}`}
            aria-disabled
            tabIndex={-1}
            onClick={e => e.preventDefault()}>
            <Abbr title={contact.email}>{contact.email}</Abbr>
          </NoteLink>
        </div>
        <RemoveButton
          type="button"
          aria-label={`Remove ${contact.firstName} ${contact.lastName}`}
          onMouseDown={e => e.stopPropagation()}
          onClick={() => onRemove(contact.email)}>
          <FontAwesomeIcon icon={faTrash} />
        </RemoveButton>
      </Content>
    );
    return reorder.type === "User" &&
      contact.department === reorder.department &&
      contact.companyName === reorder.company
      ? draggable(contact.email, index, content)
      : content;
  };

  const renderCompany = (department, company, index) => {
    const content = (
      <div key={company}>
        <Marquee text={company} />
        {grouped[department][company].map(renderContact)}
      </div>
    );
    return reorder.type === "Company" && department === reorder.department
      ? draggable(`${department}-${company}`, index, content)
      : content;
  };

  const renderDepartment = (department, index) => {
    const content = (
      <Detail key={department}>
        <DetailLabel>
          <Abbr title={department}>{department}</Abbr>
        </DetailLabel>
        <Notes>
          {Object.keys(grouped[department]).map((company, companyIndex) =>
            renderCompany(department, company, companyIndex)
          )}
        </Notes>
      </Detail>
    );
    return reorder.type === "Department" ? draggable(department, index, content) : content;
  };

  return (
    <Wrapper>
      <HeadingSmall>Contacts</HeadingSmall>
      {reorder.disabled && reorder.emptyMessage && (
        <EmptyState quiet>{reorder.emptyMessage}</EmptyState>
      )}
      {Object.keys(grouped).length ? (
        <DragDropContext onDragEnd={reorder.onDragEnd}>
          <Droppable droppableId="contact-preview">
            {provided => (
              <List ref={provided.innerRef} {...provided.droppableProps}>
                {Object.keys(grouped).map(renderDepartment)}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Text>No Contacts</Text>
      )}
    </Wrapper>
  );
};

ContactReorderPreview.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.object).isRequired,
  reorder: PropTypes.shape({
    type: PropTypes.string.isRequired,
    department: PropTypes.string,
    company: PropTypes.string,
    disabled: PropTypes.bool,
    emptyMessage: PropTypes.string,
    onDragEnd: PropTypes.func.isRequired
  }).isRequired,
  onRemove: PropTypes.func.isRequired
};

// Style Overrides
const handleGutter = pad * 2.5;

const Wrapper = styled.div`
  width: 100%;
  min-width: 330px;
  padding: ${pad}px;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
`;

const List = styled.div`
  padding-right: ${handleGutter}px;
`;

const Detail = styled.div`
  display: flex;
  justify-content: space-between;
  margin: ${pad}px 0;
`;

const DetailLabel = styled(Text)`
  width: 40%;
`;

const Notes = styled.div`
  width: 60%;
`;

const DragIndicator = styled.span`
  flex: 0 0 ${handleGutter}px;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  pointer-events: none;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const RemoveButton = styled.button`
  width: fit-content;
  flex-shrink: 0;
  padding: ${pad / 2}px;
  opacity: 0;
  transition: opacity 0.1s ease;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const DraggableRow = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: calc(100% + ${handleGutter}px);
  margin-right: -${handleGutter}px;
  border-radius: ${radius};
  cursor: grab;

  &:hover,
  &:focus-within {
    background: ${({theme}) => `${theme.primary}26`};
  }

  &:active {
    cursor: grabbing;
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${pad / 2}px 0;

  &:hover,
  &:focus-within {
    ${RemoveButton} {
      opacity: 1;
    }
  }
`;

const EmptyState = styled(Text)`
  margin: ${pad / 2}px 0 ${pad}px;
`;

const NoteText = styled(Text)`
  display: block;
  color: ${({theme}) => theme.secondary};
  word-wrap: ${({wrap}) => (wrap ? "break-word" : "normal")};
  text-overflow: ellipsis;
  white-space: ${({wrap}) => (wrap ? "unset" : "nowrap")};
  overflow: hidden;
`;

const NoteLink = styled(AnchorInline)`
  ${voice.quiet};
  display: block;
  color: ${({theme}) => theme.primary};
  word-wrap: ${({wrap}) => (wrap ? "break-word" : "normal")};
  text-overflow: ellipsis;
  white-space: ${({wrap}) => (wrap ? "unset" : "nowrap")};
  overflow: hidden;
  margin-bottom: ${pad / 2}px;
  position: relative;
  z-index: ${z("above")};

  &[aria-disabled="true"] {
    text-decoration: none;
    pointer-events: none;
  }
`;

export default ContactReorderPreview;
